import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import {
  CountryISO2CodeSchema,
  CountryISO3CodeSchema,
  LEGACY_LOCATION_STATUS,
  LOCATION_STATUS,
  LocationIdSchema,
  TOOL_UNAVAILABILITY_REASON
} from '@brightlocal/shared';

/** Reasons a tool is unavailable for a location */
export const ToolUnavailabilityReasonSchema = z.nativeEnum(TOOL_UNAVAILABILITY_REASON);

/**
 * One capability and its resolved value. `name` and `group` are open sets, so unknown values pass.
 * `name` is data (e.g. `as:location:enabled`), not a key, so it keeps its original casing.
 */
export const CapabilitySchema = z.object({
  /** Capability name (e.g. `lsg_reports_enabled`, `grc_geo_point_credits`) */
  name: z.string(),
  /** Product area the capability belongs to (e.g. `lsg`, `active_sync`) */
  group: z.string(),
  /**
   * Resolved value — a flag, a quota, a string option or a list; `null` when the plan defines none.
   * The schema doesn't tie a value shape to a `name`, so `null` passes for every capability.
   */
  value: z.union([z.boolean(), z.number().int(), z.string(), z.array(z.string())]).nullable()
});

export const CustomerCapabilitiesResponseSchema = z.object({
  /** Capabilities granted to the customer by their plan */
  data: z.array(CapabilitySchema)
});

export const LocationToolSchema = z.object({
  /** Tool code (e.g. `lsg`, `rm`, `ai_insights`); an open set */
  code: z.string(),
  /** Whether the tool can be used for this location; `true` exactly when `reasons` is empty */
  available: z.boolean(),
  /** Why the tool is unavailable */
  reasons: z.array(ToolUnavailabilityReasonSchema)
});

export const LocationCapabilitiesResponseSchema = z
  .object({
    /** Location the capabilities apply to */
    location_id: LocationIdSchema,
    /** ISO 3166-1 alpha-3 country code of the location */
    country: CountryISO3CodeSchema,
    /** Location status; parked, archived and lead are legacy values */
    status: z.union([z.nativeEnum(LOCATION_STATUS), z.nativeEnum(LEGACY_LOCATION_STATUS)]),
    /** Availability of each tool for the location */
    tools: z.array(LocationToolSchema),
    /** Capabilities of the location-based add-ons assigned to the location */
    location_capabilities: z.array(CapabilitySchema)
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export const SupportedCountrySchema = z
  .object({
    /** ISO 3166-1 alpha-3 code, as in a location's `country` */
    iso_code: CountryISO3CodeSchema,
    /** ISO 3166-1 alpha-2 code; empty string when unknown */
    iso2_code: z.union([CountryISO2CodeSchema, z.literal('')]),
    /** Whether the country is supported for location creation */
    location_creation: z.boolean(),
    /** Tool codes available in the country; an open set */
    tools: z.array(z.string())
  })
  .transform((data) => camelcaseKeys(data));

export const SupportedCountriesResponseSchema = z.object({
  /** Supported countries in BrightLocal's display order */
  data: z.array(SupportedCountrySchema)
});

export type ToolUnavailabilityReason = z.infer<typeof ToolUnavailabilityReasonSchema>;
export type Capability = z.infer<typeof CapabilitySchema>;
export type CustomerCapabilitiesResponse = z.infer<typeof CustomerCapabilitiesResponseSchema>;
export type LocationTool = z.infer<typeof LocationToolSchema>;
export type LocationCapabilitiesResponse = z.infer<typeof LocationCapabilitiesResponseSchema>;
export type SupportedCountry = z.infer<typeof SupportedCountrySchema>;
export type SupportedCountriesResponse = z.infer<typeof SupportedCountriesResponseSchema>;
